<template>
  <div class="trp">
    <header class="trp-head">
      <h1>롤테이너 태그 등록</h1>
      <div class="trp-count">이번 세션 등록 <strong>{{ registered.length }}</strong>개</div>
    </header>

    <!-- 태그 MAC -->
    <section class="trp-field">
      <label>① 태그 MAC</label>
      <div class="trp-input-row">
        <input
          ref="macInput"
          v-model="tagMac"
          @input="onMacInput"
          placeholder="태그 MAC 스캔 또는 입력"
          autocapitalize="characters"
          autocomplete="off"
        />
        <button class="scan-btn" @click="openScanner('mac')"><span>📷</span></button>
      </div>
      <div class="trp-status" :class="macStatus.cls">{{ macStatus.msg }}</div>
    </section>

    <!-- 롤테이너 바코드 -->
    <section class="trp-field">
      <label>② 롤테이너 바코드</label>
      <div class="trp-input-row">
        <input
          v-model="devicename"
          @input="onDevInput"
          inputmode="numeric"
          maxlength="10"
          placeholder="10자리 숫자 (예: 0425002470)"
          autocomplete="off"
        />
        <button class="scan-btn" @click="openScanner('dev')"><span>📷</span></button>
      </div>
      <div class="trp-status" :class="devStatus.cls">{{ devStatus.msg }}</div>
    </section>

    <button class="trp-submit" :disabled="!canRegister || submitting" @click="register">
      {{ submitting ? '등록 중...' : '등록하기' }}
    </button>
    <div v-if="resultMsg" class="trp-result" :class="resultCls">{{ resultMsg }}</div>

    <!-- 스캐너 모달 -->
    <div v-if="scanning" class="trp-scan-modal" @click.self="closeScanner">
      <div class="trp-scan-box">
        <div class="trp-scan-title">{{ scanTarget === 'mac' ? '태그 MAC 스캔' : '롤테이너 바코드 스캔' }}</div>
        <div id="trp-reader"></div>
        <button class="trp-scan-close" @click="closeScanner">닫기</button>
      </div>
    </div>

    <!-- 최근 등록 -->
    <section v-if="registered.length" class="trp-recent">
      <h2>최근 등록</h2>
      <ul>
        <li v-for="(r, i) in registered.slice().reverse()" :key="i">
          <span class="rm">{{ r.tag_mac }}</span>
          <span class="ar">→</span>
          <span class="rd">{{ r.devicename }}</span>
        </li>
      </ul>
    </section>
  </div>
</template>

<script>
import axios from 'axios';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

export default {
  name: 'TagRegisterPost',
  data() {
    return {
      tagMac: '',
      devicename: '',
      macStatus: { cls: '', msg: '' },
      devStatus: { cls: '', msg: '' },
      submitting: false,
      resultMsg: '',
      resultCls: '',
      registered: [],
      scanning: false,
      scanTarget: null,
      html5qr: null,
      macTimer: null,
      devTimer: null,
    };
  },
  computed: {
    canRegister() {
      // 태그: 신규(ok) 또는 사전등록 교체(update) / 롤테이너: 사용가능(ok)
      return (this.macStatus.cls === 'ok' || this.macStatus.cls === 'update')
        && this.devStatus.cls === 'ok';
    },
  },
  methods: {
    onMacInput() {
      this.tagMac = this.tagMac.toUpperCase().replace(/[^0-9A-F]/g, '');
      clearTimeout(this.macTimer);
      this.macStatus = { cls: '', msg: '' };
      if (this.tagMac.length < 12) {
        if (this.tagMac.length > 0) this.macStatus = { cls: 'warn', msg: '12자리 입력 필요' };
        return;
      }
      this.macTimer = setTimeout(() => this.checkMac(), 300);
    },
    onDevInput() {
      // 롤테이너 바코드는 10자리 숫자만 (관리번호, 앞자리 0 포함)
      this.devicename = this.devicename.replace(/[^0-9]/g, '').slice(0, 10);
      clearTimeout(this.devTimer);
      this.devStatus = { cls: '', msg: '' };
      if (!this.devicename) return;
      if (this.devicename.length < 10) {
        this.devStatus = { cls: 'warn', msg: `10자리 숫자 입력 필요 (현재 ${this.devicename.length}자리)` };
        return;
      }
      this.devTimer = setTimeout(() => this.checkDev(), 300);
    },
    async checkMac() {
      try {
        const r = await axios.get('/api/post/tag/check', { params: { tag_mac: this.tagMac } });
        if (r.data.tag_mac?.exists) {
          if ((r.data.tag_mac.company || '').toLowerCase() !== 'post') {
            this.macStatus = { cls: 'dup', msg: `⚠ 다른 회사(${r.data.tag_mac.company}) 태그` };
          } else {
            // 사전등록된 POST 태그 → 이름 교체 가능
            this.macStatus = { cls: 'update', msg: `↻ 사전등록됨 (${r.data.tag_mac.devicename}) → 교체 가능` };
          }
        } else {
          this.macStatus = { cls: 'ok', msg: '✓ 신규 태그' };
        }
      } catch (e) { this.macStatus = { cls: 'warn', msg: '확인 실패' }; }
    },
    async checkDev() {
      try {
        const r = await axios.get('/api/post/tag/check', { params: { devicename: this.devicename } });
        const ex = r.data.devicename;
        if (ex?.exists && ex.tag_mac !== this.tagMac) {
          this.devStatus = { cls: 'dup', msg: `⚠ 이미 등록됨 (태그 ${ex.tag_mac})` };
        } else {
          this.devStatus = { cls: 'ok', msg: '✓ 사용 가능' };
        }
      } catch (e) { this.devStatus = { cls: 'warn', msg: '확인 실패' }; }
    },
    async register() {
      if (!this.canRegister) return;
      this.submitting = true;
      this.resultMsg = '';
      try {
        const r = await axios.post('/api/post/tag/register', {
          tag_mac: this.tagMac, devicename: this.devicename,
        });
        // 두 테이블(tag_recent_web, tag_web_info) 모두 정상 반영돼야만 '완료'로 처리.
        if (!r.data.recent_ok || !r.data.info_ok) {
          const bad = [!r.data.recent_ok ? 'tag_recent_web' : null, !r.data.info_ok ? 'tag_web_info' : null].filter(Boolean).join(', ');
          throw new Error(`등록 검증 실패 — ${bad} 반영 안 됨`);
        }
        this.registered.push({ tag_mac: r.data.tag_mac, devicename: r.data.devicename });
        const mode = r.data.was_update ? `이름 교체 (${r.data.prev_name} → ${r.data.devicename})` : '신규 등록';
        this.resultMsg = `✓ ${mode} 완료 (POST 총 ${r.data.post_total}개)`;
        this.resultCls = 'ok';
        this.tagMac = '';
        this.devicename = '';
        this.macStatus = { cls: '', msg: '' };
        this.devStatus = { cls: '', msg: '' };
        this.$nextTick(() => this.$refs.macInput && this.$refs.macInput.focus());
      } catch (e) {
        this.resultMsg = '✗ ' + (e.response?.data?.error || e.message);
        this.resultCls = 'err';
      } finally {
        this.submitting = false;
      }
    },
    async openScanner(target) {
      this.scanTarget = target;
      this.scanning = true;
      await this.$nextTick();
      try {
        // 태그 MAC = QR 코드, 롤테이너 바코드 = 1D 바코드.
        // 대상에 맞는 포맷만 활성화하여 인식률·속도 향상.
        const formats = target === 'mac'
          ? [Html5QrcodeSupportedFormats.QR_CODE]
          : [
              Html5QrcodeSupportedFormats.CODE_128,
              Html5QrcodeSupportedFormats.CODE_39,
              Html5QrcodeSupportedFormats.EAN_13,
              Html5QrcodeSupportedFormats.EAN_8,
              Html5QrcodeSupportedFormats.CODE_93,
              Html5QrcodeSupportedFormats.ITF,
              Html5QrcodeSupportedFormats.CODABAR,
              Html5QrcodeSupportedFormats.UPC_A,
            ];
        this.html5qr = new Html5Qrcode('trp-reader', { formatsToSupport: formats });
        // 1D 바코드는 가로로 긴 스캔창이 인식률이 높음.
        const qrbox = target === 'mac'
          ? { width: 220, height: 220 }
          : { width: 280, height: 120 };
        await this.html5qr.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox },
          (decoded) => this.onScan(decoded),
          () => {}
        );
      } catch (e) {
        this.resultMsg = '✗ 카메라 열기 실패: ' + (e.message || e);
        this.resultCls = 'err';
        this.scanning = false;
      }
    },
    onScan(text) {
      const val = (text || '').trim();
      if (this.scanTarget === 'mac') {
        this.tagMac = val.toUpperCase().replace(/[^0-9A-F]/g, '');
        this.onMacInput();
      } else {
        this.devicename = val.replace(/[^0-9]/g, '').slice(0, 10);
        this.onDevInput();
      }
      this.closeScanner();
    },
    async closeScanner() {
      if (this.html5qr) {
        try { await this.html5qr.stop(); this.html5qr.clear(); } catch (e) { /* ignore */ }
        this.html5qr = null;
      }
      this.scanning = false;
      this.scanTarget = null;
    },
  },
  beforeUnmount() { this.closeScanner(); },
};
</script>

<style scoped>
.trp { max-width: 520px; margin: 0 auto; padding: 16px 16px 40px; font-family: 'Noto Sans KR', system-ui, sans-serif; }
.trp-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 18px; }
.trp-head h1 { font-size: 22px; font-weight: 800; margin: 0; color: #1f2937; }
.trp-count { font-size: 14px; color: #6b7280; }
.trp-count strong { color: #1f4e79; font-size: 18px; }
.trp-field { margin-bottom: 16px; }
.trp-field label { display: block; font-size: 15px; font-weight: 700; color: #374151; margin-bottom: 6px; }
.trp-input-row { display: flex; gap: 8px; }
.trp-input-row input { flex: 1; font-size: 20px; padding: 14px 12px; border: 2px solid #d1d5db; border-radius: 10px; outline: none; }
.trp-input-row input:focus { border-color: #1f4e79; }
.scan-btn { width: 58px; border: 2px solid #1f4e79; background: #1f4e79; color: #fff; border-radius: 10px; font-size: 22px; cursor: pointer; }
.trp-status { min-height: 20px; font-size: 14px; font-weight: 600; margin-top: 6px; }
.trp-status.ok { color: #059669; }
.trp-status.update { color: #2563eb; }
.trp-status.dup { color: #dc2626; }
.trp-status.warn { color: #d97706; }
.trp-submit { width: 100%; padding: 18px; font-size: 20px; font-weight: 800; color: #fff; background: #1f4e79; border: none; border-radius: 12px; margin-top: 8px; cursor: pointer; }
.trp-submit:disabled { background: #b7c2ce; cursor: not-allowed; }
.trp-result { margin-top: 14px; padding: 12px; border-radius: 10px; font-size: 16px; font-weight: 700; text-align: center; }
.trp-result.ok { background: #ecfdf5; color: #059669; }
.trp-result.err { background: #fef2f2; color: #dc2626; }
.trp-scan-modal { position: fixed; inset: 0; background: rgba(0,0,0,.7); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.trp-scan-box { background: #fff; border-radius: 14px; padding: 16px; width: 90%; max-width: 360px; }
.trp-scan-title { font-size: 16px; font-weight: 700; text-align: center; margin-bottom: 10px; }
#trp-reader { width: 100%; }
.trp-scan-close { width: 100%; margin-top: 12px; padding: 12px; border: none; background: #374151; color: #fff; border-radius: 10px; font-size: 16px; cursor: pointer; }
.trp-recent { margin-top: 26px; }
.trp-recent h2 { font-size: 16px; color: #374151; margin-bottom: 8px; }
.trp-recent ul { list-style: none; padding: 0; margin: 0; }
.trp-recent li { display: flex; align-items: center; gap: 8px; padding: 10px 12px; border-bottom: 1px solid #eef0f2; font-size: 15px; }
.rm { font-family: monospace; color: #1f2937; }
.ar { color: #9ca3af; }
.rd { font-weight: 700; color: #1f4e79; }
</style>
